// eslint-disable-next-line ember/no-computed-properties-in-native-classes
import { computed } from '@ember/object';
import {
  dominantOfficeIdForRecord,
  modalityFlagsForRecord,
} from 'client-portal/utils/clinicianSearch/send-all-catalog';
import MONARCH_ENABLED_PROFESSIONS from 'client-portal/utils/monarch-enabled-professions';
import Model, { attr, belongsTo, hasMany } from '@ember-data/model';

export default class ClinicianModel extends Model {
  @belongsTo('practice', { async: false, inverse: 'clinicians' }) practice;
  @hasMany('cpt-code', { async: false, inverse: null }) cptCodes;

  @attr('string') firstName;
  @attr('string') lastName;
  @attr('string') middleName;
  @attr('string') suffix;
  @attr('string') phone;
  @attr('string') globalProfessionName;
  @attr('string') profileImageUrl;
  @attr('string') profileTitle;
  @attr('string') nextAvailability;
  // Send-all catalog payload; an Array of opaque availability-block objects.
  // The Ember Data JSON:API serializer stores it untyped as-is.
  @attr() availabilityBlocks;
  @attr('boolean') acceptNewClientsIndicator;
  @attr() marketingSpecialties;
  @attr() marketingServices;
  @attr() marketingInsuranceProviders;
  @attr() treatmentTypes;
  @attr() ageGroups;
  @attr('boolean') acceptSelfPayCashIndicator;
  @attr('boolean') availabilityOnNightsIndicator;
  @attr('boolean') availabilityOnWeekendsIndicator;
  @attr('boolean') inPersonAppointmentsIndicator;
  @attr('boolean') freeConsultationsIndicator;

  // Derived from availabilityBlocks rather than emitted by the backend so the
  // catalog response stays small. modalityFlagsForRecord needs the office
  // records in the store to read isVideo.
  get dominantOfficeId() {
    return dominantOfficeIdForRecord(this);
  }

  get effectiveTelehealthIndicator() {
    return modalityFlagsForRecord(this, id => this.store.peekRecord('office', id)).hasTelehealth;
  }

  get effectiveInPersonAppointmentsIndicator() {
    return modalityFlagsForRecord(this, id => this.store.peekRecord('office', id)).hasInPerson;
  }

  // Keep as a classic @computed so `request/message/form`'s
  // `@reads('model.clinician.hasMonarchProfession')` re-fires when
  // `globalProfessionName` changes.
  @computed('globalProfessionName')
  get hasMonarchProfession() {
    let { globalProfessionName = '' } = this;
    return MONARCH_ENABLED_PROFESSIONS.includes(globalProfessionName?.toLowerCase?.());
  }

  get initials() {
    let f = String(this.firstName ?? '')
      .trim()
      .charAt(0);
    let l = String(this.lastName ?? '')
      .trim()
      .charAt(0);
    return `${f}${l}`.toUpperCase() || '?';
  }

  get fullName() {
    let properties = {
      firstName: this.firstName,
      lastName: this.lastName,
      middleName: this.middleName,
      suffix: this.suffix,
    };
    let { firstName, lastName, middleName, suffix } = properties;

    return [
      [firstName, middleName, lastName]
        .filter(string => string !== '')
        .compact()
        .join(' '),
      suffix !== '' ? suffix : null,
    ]
      .compact()
      .join(', ');
  }
}
